import * as React from "react";
import { useState, useEffect } from "react";

import { useApi } from "src/Api/useApi";
import { VitalsM, VitalsSummary } from "src/@types/ImpiricaPlatformApi";

import { Page, PageHero, Spinner } from "src/components/common";
import { Form, Input } from "antd";
import { Button } from "src/components/buttons";
import DropdownInputAnt from "src/components/forms/DropdownInputAnt";
import { Label } from "src/components/forms";
import { toast } from "react-toastify";

interface IProps {
	match?: any;
	history?: any;
}

export const VitalsMSummary = (props: IProps) => {
	const { organizationId, datasetId, careTestResultId, productId } =
		props.match.params;
	const { careClient, datasetsClient } = useApi();

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [vitalsM, setVitalsM] = useState<VitalsM>();

	useEffect(() => {
		if (loading === true) {
			//Load Vitals-M record
			loadData();
		}
	}, []);

	const loadData = async () => {
		if (careClient && datasetsClient) {
			const vitals = await careClient.get(
				organizationId,
				datasetId,
				careTestResultId
			);
			setVitalsM(vitals);
			setLoading(false);
		}
	};

	const convertNullIds = (value: any) => {
		if (value === null) {
			value = undefined;
		}
		return value;
	};

	const cooperativeOnChange = (value: any) => {
		const vitals = Object.assign({}, vitalsM!);
		vitals.cooperativeId = value.key;
		setVitalsM(vitals);
	};

	const visualImpairmentOnChange = (value: any) => {
		const vitals = Object.assign({}, vitalsM!);
		vitals.visualImpairmentId = value.key;
		setVitalsM(vitals);
	};

	const hearingImpairmentOnChange = (value: any) => {
		const vitals = Object.assign({}, vitalsM!);
		vitals.hearingImpairmentId = value.key;
		setVitalsM(vitals);
	};

	const physicalImpairmentOnChange = (value: any) => {
		const vitals = Object.assign({}, vitalsM!);
		vitals.physicalImpairmentId = value.key;
		setVitalsM(vitals);
	};

	const anxietyLevelOnChange = (value: any) => {
		const vitals = Object.assign({}, vitalsM!);
		vitals.anxietyLevelId = value.key;
		setVitalsM(vitals);
	};

	const commentsOnChange = (e: any) => {
		const vitals = Object.assign({}, vitalsM!);
		vitals.comments = e.target.value;
		setVitalsM(vitals);
	};

	const generateOtherAssessmentFactors = () => {
		return (
			<div>
				<div className="row">
					<div className="col-md-6 col-lg-4 form-group">
						<label>Cooperative</label>
						<Form.Item>
							<DropdownInputAnt
								id="cooperativeId"
								name="cooperativeId"
								placeholder="Cooperative"
								className="mr-3"
								pickListCode="Cooperative"
								value={convertNullIds(vitalsM!.cooperativeId)}
								sort={true}
								onChange={cooperativeOnChange}
							/>
						</Form.Item>
					</div>
				</div>
				<div>
					<label>Other Assessment Factors</label>
					<div className="row">
						<div className="col-md-6 col-lg-4 form-group">
							<label>Visual Impairment:</label>
							<Form.Item>
								<DropdownInputAnt
									id="visualImpairmentId"
									name="visualImpairmentId"
									placeholder="Visual Impairment"
									className="mr-3"
									pickListCode="VisualImpairment"
									value={convertNullIds(vitalsM!.visualImpairmentId)}
									sort={true}
									onChange={visualImpairmentOnChange}
								/>
							</Form.Item>
						</div>
						<div className="col-md-6 col-lg-4 form-group">
							<label>Hearing Impairment:</label>
							<Form.Item>
								<DropdownInputAnt
									id="hearingImpairmentId"
									name="hearingImpairmentId"
									placeholder="Hearing Impairment"
									className="mr-3"
									pickListCode="HearingImpairment"
									value={convertNullIds(vitalsM!.hearingImpairmentId)}
									sort={true}
									onChange={hearingImpairmentOnChange}
								/>
							</Form.Item>
						</div>
						<div className="col-md-6 col-lg-4 form-group">
							<label>Physical Impairment:</label>
							<Form.Item>
								<DropdownInputAnt
									id="physicalImpairmentId"
									name="physicalImpairmentId"
									placeholder="Physical Impairment"
									className="mr-3"
									pickListCode="PhysicalImpairment"
									value={convertNullIds(vitalsM!.physicalImpairmentId)}
									sort={true}
									onChange={physicalImpairmentOnChange}
								/>
							</Form.Item>
						</div>
						<div className="col-md-6 col-lg-4 form-group">
							<label>Anxiety Level:</label>
							<Form.Item>
								<DropdownInputAnt
									id="anxietyLevelId"
									name="anxietyLevelId"
									placeholder="Anxiety Level"
									className="mr-3"
									pickListCode="AnxietyLevel"
									value={convertNullIds(vitalsM!.anxietyLevelId)}
									sort={true}
									onChange={anxietyLevelOnChange}
								/>
							</Form.Item>
						</div>
					</div>
				</div>
			</div>
		);
	};

	const goBack = () => {
		props.history.goBack();
	};

	const save = async () => {
		try {
			setSaving(true);
			if (vitalsM) {
				await careClient!.updateSummary(
					new VitalsSummary({ ...vitalsM }),
					organizationId,
					datasetId,
					vitalsM.clientId.toString()
				);
				toast.success("Saved");
				goBack();
			}
		} catch (e) {
			console.error(e);
			toast.error("Something went wrong...");
		} finally {
			setSaving(false);
		}
	};

	return loading ? (
		<Spinner />
	) : (
		<Page>
			<PageHero>
				<h1 className="mb-0">Vitals Summary</h1>
			</PageHero>
			<div className="container-fluid">
				{productId === "DriveABLE" && generateOtherAssessmentFactors()}
				<div className="form-group">
					<Label>Comments:</Label>
					<Input.TextArea
						id="comments"
						name="comments"
						placeholder="Comments"
						value={vitalsM!.comments}
						onChange={commentsOnChange}
					>
						{vitalsM!.comments}
					</Input.TextArea>
				</div>
			</div>
			<div className="form-group">
				<div>
					<Button
						className="btn-primary ml-3"
						onClick={goBack}
						disabled={saving}
					>
						Back
					</Button>
					<Button className="btn-primary ml-3" onClick={save} disabled={saving}>
						Save
					</Button>
				</div>
			</div>
		</Page>
	);
};
